import { createStore, useStore } from '../state/store'

/**
 * Dark by default, light on request, remembered per device.
 *
 * `index.html` applies the saved choice before the first paint, so a light
 * install never flashes charcoal on launch; this module only takes over once
 * React is running.
 */
export type Theme = 'dark' | 'light'

const KEY = 'content:theme'
const BACKGROUND: Record<Theme, string> = { dark: '#191715', light: '#faf7f2' }

function saved(): Theme {
  try {
    return localStorage.getItem(KEY) === 'light' ? 'light' : 'dark'
  } catch {
    return 'dark'
  }
}

export const themeStore = createStore<Theme>(typeof window === 'undefined' ? 'dark' : saved())

export const useTheme = () => useStore(themeStore)

export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'light') root.setAttribute('data-theme', 'light')
  else root.removeAttribute('data-theme')
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', BACKGROUND[theme])
}

export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(KEY, theme)
  } catch {
    // Private mode: the choice lasts for this visit only.
  }
  applyTheme(theme)
  themeStore.set(theme)
}
